/**
 * CR001: Hook to fetch Spend & Earn offers
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SpendEarnOffer } from "@/lib/types/spendEarnOffers";

interface UseSpendEarnOffersResult {
  offers: SpendEarnOffer[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSpendEarnOffers(): UseSpendEarnOffersResult {
  const [offers, setOffers] = useState<SpendEarnOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOffers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('spend_earn_offers')
        .select('*')
        .order('normalized_value_score', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      // Type assertion since we know the structure
      setOffers((data || []) as unknown as SpendEarnOffer[]);
    } catch (err) {
      console.error('Error fetching spend & earn offers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  return {
    offers,
    loading,
    error,
    refetch: fetchOffers,
  };
}
